import { FORUM } from 'constants.js'
import _ from 'lodash'
import axios from 'axios'
import axiosRetry from 'axios-retry'
import { generateDeviceFingerprint } from 'fingerprint'
import { isHttpSuccess } from 'utils'
import { getFormattedUTCTime } from 'utils/date'
import { showHasStarted } from 'utils/show'

axiosRetry(axios, {
    retries: 3,
    retryDelay: retryCount => {
        return retryCount * 1000
    },
})

const axiosGet = (url, additionalPath) => async () =>
    _.get(await axios.get(url), additionalPath ? `data.${additionalPath}` : 'data')

export const fetchPaymentMethods = axiosGet('/api/v1/payment/methods', 'paymentMethods')

export const deletePaymentMethod = token => axios.delete(`/api/v1/payment/methods/${token}`)

export const fetchAllPasses = axiosGet('/api/v1/passes', 'passes')

export const patchUser = async newUser => {
    let resp = await axios.patch('/api/v1/user', newUser)
    if (!resp || !isHttpSuccess(resp)) throw new Error('User not updated')
    return resp.data
}

export const postSignin = body => axios.post('/api/v1/auth/sign-in', body)

export const postSignup = body => axios.post('/api/v1/auth/sign-up', body)

export const fetchSignout = () => axios.get('/api/v1/auth/sign-out')

export const fetchArtistShow = async id => _.get(await axios(`/api/v1/artist/show/${id}`), 'data.show')

export const fetchShowVideo = async id => _.get(await axios.get(`/api/v1/show/${id}/video`), 'data')

export const fetchForumEntries = async (
    showId,
    { count = FORUM.FORUM_MAX_ENTRIES_IN_MEMORY, sort = FORUM.FORUM_SORT_TOP, page = null, acknowledged = false } = {},
) => {
    try {
        let query = `${showId}?count=${count}&sort=${sort}&acknowledged=${acknowledged}`
        if (page) query += `&page=${page}`

        return _.get(await axios.get(`/api/v1/forum/entry/show/${query}`), 'data.entries') || []
    } catch (err) {
        return []
    }
}

export const postShowViewer = async (show, wsChannel) => {
    try {
        let body = {
            showId: show._id,
            socketId: _.get(wsChannel, 'pusher.connection.socket_id'),
            previewStartedAt: !showHasStarted(show) ? show.startTime : getFormattedUTCTime(),
            fingerprint: await generateDeviceFingerprint(),
        }
        return _.get(await axios.post('/api/v1/show/viewer', body), 'data')
    } catch (err) {
        return { viewerCount: null, remainingPasses: undefined }
    }
}

export const getShowViewerByFingerprint = async ({ showId, fingerprint }) =>
    _.get(await axios.get(`/api/v1/shows/${showId}/viewers/fingerprint/${fingerprint}`), 'data')

export const getShowViewerBreakdown = async showId =>
    _.get(await axios.get(`/api/v1/shows/${showId}/viewers/breakdown`), 'data')

export const patchUpVote = async entryId => _.get(await axios.patch(`/api/v1/forum/entry/${entryId}`), 'data')

export const patchForumAcknowledged = async entryId =>
    _.get(await axios.patch(`/api/v1/forum/entry/${entryId}`, { acknowledged: true }), 'data')

export const patchForumUnacknowledged = async entryId =>
    _.get(await axios.patch(`/api/v1/forum/entry/${entryId}`, { acknowledged: false }), 'data')

export const postForumEntry = async body => _.get(await axios.post('/api/v1/forum/entry', body), 'data')

export const fetchPassListForCurrentUserOrSession = async showId =>
    _.get(await axios.get(`/api/v1/passes?showId=${showId}`), 'data') || []

export const fetchPaymentTokenReq = async () => _.get(await axios.get('/api/v1/payment/token'), 'data.token')

export const postNewTransactionReq = async data => _.get(await axios.post(`/api/v1/payment/transaction`, data), 'data')

export const patchRedeemCodeReq = async (showId, code) =>
    _.get(await axios.patch(`/api/v1/passes/code/${code}?showId=${showId}`), 'data')

export const fetchChannelMessages = async (channelName, limit, timestamp) => {
    const resp = await axios.get(`/api/v1/chat/channel/${channelName}?limit=${limit}&created-lt=${timestamp}`)
    const messages = _.get(resp, 'data', [])

    return _.reduceRight(
        messages,
        (acc, msg) =>
            acc.concat({
                msgId: msg.msgId,
                userId: msg.userId,
                displayName: msg.displayName,
                body: msg.body,
                timestamp: msg.timestamp,
                likeUsers: msg.likeUsers,
                chatColor: msg.chatColor,
            }),
        [],
    )
}

export const updateMessageLike = async (msgId, userId) =>
    await axios.patch(`/api/v1/chat/message/${msgId}`, { likeUserId: userId })

export const fetchPriorityListArtist = async artistId =>
    _.get(await axios.get(`/api/v1/priority-list/${artistId}`), 'data')

export const postPriorityListUser = async (artistId, body) =>
    await axios.post(`/api/v1/priority-list/${artistId}/user`, body)

export const patchPriorityListUser = async (artistId, userId, body) =>
    _.get(await axios.patch(`/api/v1/priority-list/${artistId}/user/${userId}`, body), 'data')

export const fetchUserIPv4 = async () => {
    try {
        let resp = await axios.create({ baseURL: 'https://api.ipify.org', withCredentials: false }).get(`/?format=json`)
        return _.get(resp, 'data.ip') || ''
    } catch (err) {
        return ''
    }
}

export const postTribute = async body => await axios.post('/api/v1/fan-mail', body)

export const postShowNote = async (showId, note) =>
    _.get(await axios.post(`/api/v1/artist/show/${showId}/notes`, note), 'data')

export const postChatMessage = async message =>
    _.get(await axios.post(`/api/v2/chat/message`, message, { withCredentials: false }), 'data')

export const postWordFlowMessage = async body => _.get(await axios.post(`/api/v1/word-flow/message`, body), 'data')

export const getLUAAsset = async url => {
    const resp = await axios.create({ withCredentials: false }).get(url, {
        headers: {
            accept: 'image/svg+xml',
        },
    })
    const contentType = _.get(resp, 'headers["content-type"]')

    if (contentType !== 'image/svg+xml') {
        //TODO we can remove this check when axios gives us a way to remove the default headers which (our Accept headers are set to */*)
        console.error(`Content type ${contentType} is not allowed for LUA assets. Expected image/svg+xml`)
    } else {
        return resp && { imgUrl: url, svg: resp.data }
    }
}
